'use client';

import React, { useState } from 'react';
import { useDarkMode } from '@/lib/DarkModeContext';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

interface Props {
    title: string;
    url?: string;
    image?: string;
    compact?: boolean;
}

export default function ShareButtons({ title, url, image, compact }: Props) {
    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();
    const [copied, setCopied] = useState(false);

    const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

    const openPopup = (shareUrl: string) => {
        window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=500');
    };

    const shareFacebook = () => {
        const u = encodeURIComponent(pageUrl);
        const q = encodeURIComponent(title || '');
        openPopup(`https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${q}`);
    };

    const shareTwitter = () => {
        const text = encodeURIComponent(title || '');
        const u = encodeURIComponent(pageUrl);
        openPopup(`https://twitter.com/intent/tweet?text=${text}&url=${u}`);
    };

    const shareLinkedIn = () => {
        const u = encodeURIComponent(pageUrl);
        openPopup(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`);
    };

    const shareWhatsApp = () => {
        const txt = encodeURIComponent(`${title} ${pageUrl}`);
        openPopup(`https://wa.me/?text=${txt}`);
    };

    const shareInstagram = async () => {
        // Instagram does not support direct web share to feed from desktop.
        // Try native Web Share API (mobile will open Instagram if available), else copy link for manual sharing.
        if (navigator.share) {
            try {
                await navigator.share({ title, text: title, url: pageUrl });
            } catch (e) {
                // ignore
            }
        } else {
            await copyLink();
            alert(siteLang === 'en'
                ? 'Instagram does not support direct sharing from the web. The link has been copied — paste it in an Instagram message.'
                : 'Instagram ne podržava direktno deljenje sa web-a. Link je kopiran, nalepite ga u Instagram poruku.');
        }
    };

    const copyLink = async () => {
        if (!pageUrl) return;
        try {
            await navigator.clipboard.writeText(pageUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            alert(siteLang === 'en' ? 'Cannot copy link.' : 'Ne mogu da kopiram link.');
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) {
            alert(siteLang === 'en' ? 'Native share is not available in this browser.' : 'Native share nije dostupan u ovom browseru.');
            return;
        }

        try {
            // Try to include image if possible
            if (image && 'canShare' in navigator && (navigator as any).canShare && typeof (navigator as any).canShare === 'function') {
                try {
                    const res = await fetch(image);
                    const blob = await res.blob();
                    const file = new File([blob], 'image.jpg', { type: blob.type });
                    const shareData: any = { title, text: title, url: pageUrl, files: [file] };
                    if ((navigator as any).canShare({ files: [file] })) {
                        await (navigator as any).share(shareData);
                        return;
                    }
                } catch (e) {
                    // ignore image sharing errors and fall back to url share
                }
            }

            await navigator.share({ title, text: title, url: pageUrl });
        } catch (e) {
            // user cancelled or failed
        }
    };

    const baseBtn = `${compact ? 'text-xs px-2 py-1' : 'text-sm px-3 py-2'} rounded-lg font-medium`;
    const themeBg = darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 shadow-sm';

    return (
        <div className={`flex ${compact ? 'gap-2 items-center' : 'gap-3 flex-wrap items-center'}`}>
            <button onClick={shareFacebook} className={`${baseBtn} ${themeBg} hover:opacity-90`}>📘 Facebook</button>
            <button onClick={shareInstagram} className={`${baseBtn} ${themeBg} hover:opacity-90`}>📸 Instagram</button>
            <button onClick={shareTwitter} className={`${baseBtn} ${themeBg} hover:opacity-90`}>🐦 Twitter</button>
            <button onClick={shareLinkedIn} className={`${baseBtn} ${themeBg} hover:opacity-90`}>🔗 LinkedIn</button>
            <button onClick={shareWhatsApp} className={`${baseBtn} ${themeBg} hover:opacity-90`}>💬 WhatsApp</button>
            <button onClick={copyLink} className={`${baseBtn} ${themeBg} hover:opacity-90`}>{copied ? (siteLang === 'en' ? '✅ Copied' : '✅ Kopirano') : (siteLang === 'en' ? '🔗 Copy link' : '🔗 Kopiraj link')}</button>
            {'share' in navigator && (
                <button onClick={nativeShare} className={`${baseBtn} ${themeBg} hover:opacity-90`}>{siteLang === 'en' ? '📤 Share' : '📤 Deljenje'}</button>
            )}
        </div>
    );
}
